import { Controller } from '@hotwired/stimulus'

function download(file) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(file);
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export default class extends Controller {
  static targets = [
    'viewer',
    'inputFile',
    'panel',
    'areaEnabledCheckbox',
    'areaVisibleCheckbox',
    'lengthEnabledCheckbox',
    'lengthVisibleCheckbox',
    'deleteAllButton'
  ]

  THREE = null
  OBC = null
  components = null
  mainWorld = null
  areaMeasurement = null
  lengthMeasurement = null

  Panel = (world) => this.BUI.Component.create(() => {
    return this.BUI.html`
      <bim-panel label="Worlds Tutorial" class="options-menu">
        <bim-panel-section collapsed label="Controls">

          <bim-button label="Load IFC"
            @click="${() => this.loadIfc()}">
          </bim-button>

          <bim-button label="Export fragments"
            @click="${() => this.exportFragments()}">
          </bim-button>

          <bim-button label="Dispose fragments"
            @click="${() => this.disposeFragments()}">
          </bim-button>

          <bim-color-input
            label="Background Color" color="#202932"
            @input="${({ target }) => this.changeBackground(target.color)}">
          </bim-color-input>

          <bim-number-input
            slider step="0.1" label="Directional lights intensity" value="1.5" min="0.1" max="10"
            @change="${({ target }) => this.changeDirectionalLightIntensity(target.value)}">
          </bim-number-input>

          <bim-number-input
            slider step="0.1" label="Ambient light intensity" value="1" min="0.1" max="5"
            @change="${({ target }) => this.changeAmbientLightIntensity(target.value)}">
          </bim-number-input>

          <bim-button label="Toggle Wireframe" @click="${() => this.toggleWireframe()}" />
          <bim-button label="Reset View" @click="${() => this.resetView()}" />
          <bim-button label="Clear All" @click="${() => this.clearAll()}" />
        </bim-panel-section>

        <bim-panel active label="Area Measurement Tutorial" class="options-menu">
          <bim-panel-section collapsed label="Controls">
              <bim-label>Create area dimension: Double click</bim-label>
              <bim-label>Calculate selected area: Right click</bim-label>
              <bim-label>Delete  dimension: Delete</bim-label>
          </bim-panel-section>

          <bim-panel-section collapsed label="Others">
            <bim-checkbox checked label="Area dimensions enabled"
              @change="${({ target }) => {
                this.areaDims.enabled = target.value;
              }}">
            </bim-checkbox>
            <bim-checkbox checked label="Area dimensions visible"
              @change="${({ target }) => {
                this.areaDims.visible = target.value;
              }}">
            </bim-checkbox>

            <bim-button label="Delete all"
              @click="${() => {
                this.areaDims.deleteAll();
              }}">
            </bim-button>

          </bim-panel-section>
        </bim-panel>
      </bim-panel>
    `;
  });

  async connect() {
    this.THREE = await import('https://cdn.jsdelivr.net/npm/three@0.160.1/+esm')
    this.OBC = await import('https://cdn.jsdelivr.net/npm/@thatopen/components@2.4.4/+esm')
    this.OBCF = await import('https://cdn.jsdelivr.net/npm/@thatopen/components-front@2.4.4/+esm')
    this.BUI = await import('https://cdn.jsdelivr.net/npm/@thatopen/ui@2.4.2/+esm')
    this.WEBIFC = await import('https://cdn.jsdelivr.net/npm/web-ifc@0.0.68/+esm')
    this.BUI.Manager.init();
    await this.initViewer()
  }

  async initViewer() {
    this.panelTarget.append(this.Panel(this.mainWorld));

    const viewerContainer = this.viewerTarget
    this.components = new this.OBC.Components()

    const world = this.components.get(this.OBC.Worlds)
    this.mainWorld = world.create()

    this.mainWorld.scene = new this.OBC.SimpleScene(this.components)
    // this.mainWorld.renderer = new this.OBC.SimpleRenderer(this.components, viewerContainer)
    this.mainWorld.renderer = new this.OBCF.PostproductionRenderer(this.components, viewerContainer)
    this.mainWorld.camera = new this.OBC.SimpleCamera(this.components)
    this.components.init()

    this.mainWorld.camera.controls.setLookAt(5, 5, 5, 0, 0, 0)
    this.mainWorld.scene.setup()

    // start fragments
    this.fragments = this.components.get(this.OBC.FragmentsManager);
    this.fragmentIfcLoader = this.components.get(this.OBC.IfcLoader);
    await this.fragmentIfcLoader.setup();
    const excludedCategories = [
      this.WEBIFC.IFCTENDONANCHOR,
      this.WEBIFC.IFCREINFORCINGBAR,
      this.WEBIFC.IFCREINFORCINGELEMENT,
    ];

    for (const cat of excludedCategories) {
      this.fragmentIfcLoader.settings.excludedCategories.add(cat);
    }
    this.fragmentIfcLoader.settings.webIfc.COORDINATE_TO_ORIGIN = true;
    // end Fragments

    const grid = this.components.get(this.OBC.Grids)
    grid.create(this.mainWorld)

    this.mainWorld.scene.three.background = null

    const geometry = new this.THREE.BoxGeometry(3, 3, 3)
    const material = new this.THREE.MeshStandardMaterial({ color: "#6528D7" })
    const cube = new this.THREE.Mesh(geometry, material)
    cube.position.set(0, 1.5, 0)
    this.mainWorld.scene.three.add(cube)

    if (!this.mainWorld.meshes) {
      this.mainWorld.meshes = new Set()
    }
    this.mainWorld.meshes.add(cube)
    this.setupAreaMeasurement()
  }

  setupAreaMeasurement() {
    this.areaDims = this.components.get(this.OBCF.AreaMeasurement);
    this.areaDims.world = this.mainWorld;
    this.areaDims.enabled = true;
    this.viewerTarget.ondblclick = () => this.areaDims.create();
    this.viewerTarget.oncontextmenu = () => this.areaDims.endCreation();
  }

  async loadIfc() {
    const file = await fetch("https://thatopen.github.io/engine_components/resources/small.ifc");
    const data = await file.arrayBuffer();
    const buffer = new Uint8Array(data);
    const model = await this.fragmentIfcLoader.load(buffer);
    model.name = "example";

    this.mainWorld.scene.three.add(model);
  }

  async exportFragments() {
    if (!this.fragments.groups.size) {
      return;
    }
    const group = Array.from(this.fragments.groups.values())[0];
    const data = this.fragments.export(group);
    download(new File([new Blob([data])], "small.frag"));

    const properties = group.getLocalProperties();
    if (properties) {
      download(new File([JSON.stringify(properties)], "small.json"));
    }
  }

  disposeFragments() {
    this.fragments.dispose();
  }

  changeBackground(color) {
    if (!this.mainWorld || !this.mainWorld.scene) return

    this.mainWorld.scene.three.background = new this.THREE.Color(color)
  }

  changeDirectionalLightIntensity(value) {
    if (!this.mainWorld || !this.mainWorld.scene) return

    this.mainWorld.scene.config.directionalLight.intensity = value
  }

  changeAmbientLightIntensity(value) {
    if (!this.mainWorld || !this.mainWorld.scene) return

    this.mainWorld.scene.config.ambientLight.intensity = value
  }

  toggleWireframe() {
    if (!this.mainWorld || !this.mainWorld.meshes) return

    for (const mesh of this.mainWorld.meshes) {
      if (mesh.material) {
        mesh.material.wireframe = !mesh.material.wireframe
      }
    }
  }

  resetView() {
    if (!this.mainWorld || !this.mainWorld.camera) return

    this.mainWorld.camera.controls.setLookAt(5, 5, 5, 0, 0, 0)
    this.mainWorld.camera.controls.update()
  }

  clearAll() {
  }
}
